// Componente de encabezado del dashboard
// Dashboard header component

import { useTranslation } from 'react-i18next';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { useShops } from '../../hooks/useShops';

interface DashboardHeaderProps {
  shopId: string;
  cutoffTime: string;
  onChangeShop: (value: string) => void;
  onChangeCutoffTime: (value: string) => void;
}

export const DashboardHeader = ({ shopId, cutoffTime, onChangeShop, onChangeCutoffTime }: DashboardHeaderProps) => {
  const { t, i18n } = useTranslation('homeModule');
  const { shopOptions, isLoading } = useShops();

  // Obtener fecha actual formateada según el idioma
  const getCurrentDate = () => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();

    // Formato según idioma: DD/MM/YYYY para español, MM/DD/YYYY para inglés
    return i18n.language === 'es' 
      ? `${day}/${month}/${year}`
      : `${month}/${day}/${year}`;
  };

  // Generar opciones de hora de corte en incrementos de 30 minutos
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        options.push({ label: timeString, value: timeString });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Handlers para los cambios de filtros
  const handleShopChange = (value: string) => {
    onChangeShop(value);
  };

  const handleCutoffTimeChange = (value: string) => {
    onChangeCutoffTime(value);
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
      {/* Título y subtítulo */}
      <div className="flex-1">
        <h1 className="text-xl font-bold text-title">{t('welcome.title')}</h1>
        <p className="text-complementary-text text-sm">
          {t('welcome.subtitle')} - {getCurrentDate()}
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:w-48">
          <Combobox
            options={shopOptions}
            value={shopId}
            setValue={handleShopChange}
            placeholder={t('filters.store')}
            dataTestId="store-filter"
            disabled={isLoading}
          />
        </div>
        <div className="w-full sm:w-48">
          <Combobox
            options={timeOptions}
            value={cutoffTime}
            setValue={handleCutoffTimeChange}
            placeholder={t('filters.cutoff_time')}
            dataTestId="cutoff-time-filter"
          />
        </div>
      </div>
    </div>
  );
};
